import { QueryClient, QueryFunctionContext, QueryKey, useQuery, useQueryClient } from '@tanstack/react-query'
import { Identity, Maybe, Organization, OrgWhiteLabel, SimpleMap, VendorGroup } from '@vendorflow/types'
import { QueryCacheEnum } from 'constants/ReactQuery.constants'
import {
  fetchManagedIdentitiesUsingGET,
  fetchManagedOrgsUsingGET,
  fetchOrganizationsUsingGET,
  fetchOrganizationUsingGET,
  fetchManagedOrgMembersUsingGET,
  fetchOrgsVendorGroupsUsingGET,
  fetchWhiteLabelForOrgUsingGET,
  fetchOrgMembersUsingGET,
  fetchRootOrganizationUsingGET,
  fetchEnviesWithSameRootUsingGET,
} from 'apis/Organizations.api'
import { UseQueryOptions } from 'types/ReactQuery.type'
import { convertResourceToEntity } from '@vendorflow/common/lib/converters/JsonApi.converter'
import { sortBy } from 'lodash'
import { useMemo } from 'react'
import { keyBy } from 'lodash'
import { useCognitoRegisteredAccounts } from 'features/cognito/CognitoUsers.hooks'
import { useMyOrgId } from 'queries/Auth.queries'
import ms from 'ms'
import { IdentityWithRels } from 'types/Azimuth.types'

export function useOrganization(id: Maybe<string>, options?: UseQueryOptions) {
  const enabled = options?.enabled !== undefined ? options.enabled : true

  return useQuery<Organization, Error>([QueryCacheEnum.ORGANIZATIONS, id], () => fetchOrganizationUsingGET(id!), {
    enabled: !!id && enabled,
  })
}

export function useOrganizations(options?: UseQueryOptions) {
  const enabled = options?.enabled !== undefined ? options.enabled : true

  return useQuery<Organization[], Error>([QueryCacheEnum.ORGANIZATIONS], () => fetchOrganizationsUsingGET(), {
    enabled,
  })
}

/**
 * Alices in Managed Org (PreviousTrucking)
 */
export function alicesForManagedOrgQueryKey(id: Maybe<string>) {
  return [QueryCacheEnum.MANAGED_ORGS, id, QueryCacheEnum.MANAGED_MEMBERS]
}

export async function alicesForManagedOrgQueryFn({
  queryKey,
}: {
  queryKey: ReturnType<typeof alicesForManagedOrgQueryKey>
}) {
  const managedOrg = queryKey[1]!
  return fetchManagedOrgMembersUsingGET(managedOrg)
}

export function useAlicesForManagedOrg(id: Maybe<string>, options?: UseQueryOptions) {
  const enabled = options?.enabled !== undefined ? options.enabled : true

  return useQuery<Identity[], Error, Identity[], ReturnType<typeof alicesForManagedOrgQueryKey>>(
    alicesForManagedOrgQueryKey(id),
    alicesForManagedOrgQueryFn,
    { enabled: !!id && enabled }
  )
}

/**
 * Bobs in Envy
 */
export function bobsInEnvyQueryKey(envy: Maybe<string>) {
  return [QueryCacheEnum.ORGANIZATIONS, envy, QueryCacheEnum.MEMBERS]
}

export async function bobsInEnvyQueryFn({ queryKey }: { queryKey: ReturnType<typeof bobsInEnvyQueryKey> }) {
  return fetchOrgMembersUsingGET(queryKey[1]!)
}

export function useBobsInEnvy(id: Maybe<string>, options?: UseQueryOptions) {
  const enabled = options?.enabled !== undefined ? options.enabled : true

  return useQuery<IdentityWithRels[], Error, IdentityWithRels[], ReturnType<typeof bobsInEnvyQueryKey>>(
    bobsInEnvyQueryKey(id),
    bobsInEnvyQueryFn,
    {
      enabled: !!id && enabled,
      staleTime: ms('5m'),
      cacheTime: ms('5m'),
    }
  )
}

export function useBobsInEnvyById(id: Maybe<string>) {
  const result = useBobsInEnvy(id)
  const data = useMemo<Record<string, Identity>>(() => {
    return keyBy(result.data, 'id')
  }, [result.dataUpdatedAt])

  return { data, dataUpdatedAt: result.dataUpdatedAt }
}

export function useOrgWhitelabel(id: Maybe<string>, options?: UseQueryOptions) {
  const enabled = options?.enabled !== undefined ? options.enabled : true

  return useQuery<OrgWhiteLabel, Error>(
    [QueryCacheEnum.ORGANIZATIONS, id, QueryCacheEnum.WHITELABEL],
    () => fetchWhiteLabelForOrgUsingGET(id!),
    { enabled: !!id && enabled, retry: false }
  )
}

/**
 * All Managed Orgs in Envy Query
 */
export function managedOrgsInEnvyQueryKey(envyId: Maybe<string>) {
  return [QueryCacheEnum.ORGANIZATIONS, envyId, QueryCacheEnum.MANAGED_ORGS]
}

export function managedOrgsInEnvyQueryFn({ queryKey }: { queryKey: ReturnType<typeof managedOrgsInEnvyQueryKey> }) {
  const envy = queryKey[1]!
  return fetchManagedOrgsUsingGET(envy)
}

export function useManagedOrgsInEnvy(id: Maybe<string>, options?: UseQueryOptions) {
  const enabled = options?.enabled !== undefined ? options.enabled : true

  return useQuery<Organization[], Error, Organization[], ReturnType<typeof managedOrgsInEnvyQueryKey>>(
    managedOrgsInEnvyQueryKey(id),
    managedOrgsInEnvyQueryFn,
    {
      enabled: !!id && enabled,
    }
  )
}

/**
 * All Alices In Envy Query
 */
export function alicesForEnvyQueryKey(
  envyId: Maybe<string>
): [QueryCacheEnum.ORGANIZATIONS, Maybe<string>, QueryCacheEnum.ALICES] {
  return [QueryCacheEnum.ORGANIZATIONS, envyId, QueryCacheEnum.ALICES]
}

export async function alicesForEnvyQueryFn({
  queryKey,
}: QueryFunctionContext<ReturnType<typeof alicesForEnvyQueryKey>>): Promise<Identity[]> {
  const envy = queryKey[1]!
  const result = await fetchManagedIdentitiesUsingGET(envy)
  return result.data.data.map((item) => convertResourceToEntity(item))
}

export function useAlicesForEnvy(envyId: Maybe<string>, options?: UseQueryOptions) {
  const enabled = options?.enabled !== undefined ? options.enabled : true

  return useQuery<Identity[], Error, Identity[], ReturnType<typeof alicesForEnvyQueryKey>>(
    alicesForEnvyQueryKey(envyId),
    alicesForEnvyQueryFn,
    {
      enabled: !!envyId && enabled,
      staleTime: ms('5m'),
    }
  )
}

export function useAlicesInEnvyById(id: Maybe<string>) {
  const result = useAlicesForEnvy(id)
  const data = useMemo<SimpleMap<Identity>>(() => {
    return keyBy(result.data, 'id')
  }, [result.dataUpdatedAt])

  return { data, dataUpdatedAt: result.dataUpdatedAt }
}

/**
 * Managed Org By Alice Query
 */
export function managedOrgsByAliceQueryKey(envyId: Maybe<string>) {
  return [QueryCacheEnum.ORGANIZATIONS, envyId, QueryCacheEnum.MANAGED_ORGS_BY_ALICE]
}

export async function managedOrgsByAliceQueryFn({
  context,
  queryClient,
}: {
  context: QueryFunctionContext<QueryKey> // QueryKey typing sucks & won't let me use ReturnType of query key function
  queryClient: QueryClient
}) {
  const envy = context.queryKey[1] as string
  const managedOrgs = await queryClient.fetchQuery(managedOrgsInEnvyQueryKey(envy), managedOrgsInEnvyQueryFn)
  const results = await Promise.allSettled(
    managedOrgs.map(async (mo) => {
      const result = await queryClient.fetchQuery(alicesForManagedOrgQueryKey(mo.id), alicesForManagedOrgQueryFn)
      return { orgId: mo.id, identities: result }
    })
  )
  const identitiesMap: SimpleMap<string> = {}

  results.forEach((item) => {
    if (item.status === 'fulfilled') {
      item.value.identities.forEach((identity) => {
        identitiesMap[identity.id] = item.value.orgId
      })
    }
  })

  return identitiesMap
}

export function useManagedOrgsByAlice(envyId: Maybe<string>, options?: UseQueryOptions) {
  const queryClient = useQueryClient()
  const enabled = options?.enabled !== undefined ? options.enabled : true

  return useQuery<SimpleMap<string>, Error>(
    managedOrgsByAliceQueryKey(envyId!),
    (context) => managedOrgsByAliceQueryFn({ context, queryClient }),
    { enabled: !!envyId && enabled }
  )
}

/**
 * All Vendor Groups in Envy
 */
export function vendorGroupsForEnvyQueryKey(envyId: Maybe<string>) {
  return [QueryCacheEnum.ORGANIZATIONS, envyId!, QueryCacheEnum.VENDOR_GROUPS]
}

export async function vendorGroupsForEnvyQueryFn({
  queryKey,
}: {
  queryKey: ReturnType<typeof vendorGroupsForEnvyQueryKey>
}) {
  const envy = queryKey[1]!
  const result = await fetchOrgsVendorGroupsUsingGET(envy)
  return sortBy(result, 'name')
}

export function useOrgVendorGroups(orgId: Maybe<string>, options?: UseQueryOptions) {
  const enabled = options?.enabled !== undefined ? options.enabled : true

  return useQuery<VendorGroup[], Error, VendorGroup[], ReturnType<typeof vendorGroupsForEnvyQueryKey>>(
    vendorGroupsForEnvyQueryKey(orgId),
    vendorGroupsForEnvyQueryFn,
    { enabled: !!orgId && enabled }
  )
}

export function useEnabledBobs() {
  const envy = useMyOrgId()
  const cognitoData = useCognitoRegisteredAccounts(envy)
  const data = useMemo(() => {
    return cognitoData.registeredAccounts.filter((account) => account.cognitoEnabled)
  }, [cognitoData.registeredAccounts, cognitoData.dataUpdatedAt])
  return { data, dataUpdatedAt: cognitoData.dataUpdatedAt }
}

export function rootOrganizationQueryKey(envy: Maybe<string>) {
  return [QueryCacheEnum.ORGANIZATIONS, envy, QueryCacheEnum.ROOT]
}

export async function rootOrganizationQueryFn({ queryKey }: { queryKey: ReturnType<typeof rootOrganizationQueryKey> }) {
  const envy = queryKey[1]!
  return fetchRootOrganizationUsingGET(envy)
}

export function useRootOrganizationQuery() {
  const envy = useMyOrgId()

  return useQuery<string, Error, string, ReturnType<typeof rootOrganizationQueryKey>>({
    queryKey: rootOrganizationQueryKey(envy),
    queryFn: rootOrganizationQueryFn,
    enabled: Boolean(envy),
    staleTime: Infinity,
    cacheTime: Infinity,
  })
}

export function organizationsWithSameRootQueryKey(
  envy: Maybe<string>
): [QueryCacheEnum.ORGANIZATIONS, Maybe<string>, QueryCacheEnum.SAME_ROOT, QueryCacheEnum.ORGANIZATIONS] {
  return [QueryCacheEnum.ORGANIZATIONS, envy, QueryCacheEnum.SAME_ROOT, QueryCacheEnum.ORGANIZATIONS]
}

export async function organizationsWithSameRootQueryFn({
  queryKey,
}: QueryFunctionContext<ReturnType<typeof organizationsWithSameRootQueryKey>>): Promise<Organization[]> {
  const envy = queryKey[1]!
  return fetchEnviesWithSameRootUsingGET(envy)
}

export function useEnviesWithSameRootQuery() {
  const envy = useMyOrgId()

  return useQuery<Organization[], Error, Organization[], ReturnType<typeof organizationsWithSameRootQueryKey>>({
    queryKey: organizationsWithSameRootQueryKey(envy),
    queryFn: organizationsWithSameRootQueryFn,
    enabled: Boolean(envy),
  })
}
